/*
 * Portions of this code are from Novel (https://github.com/steven-tey/novel)
 * Copyright (c) 2023 Steven Tey
 * Licensed under the Apache License, Version 2.0
 */

import { Image } from '@tiptap/extension-image'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'

const UpdatedImage = Image.extend({
  name: 'image',
  addAttributes() {
    // Explicitly define all attributes with parseHTML/renderHTML for proper serialization
    // TipTap will merge these with the base Image extension attributes
    return {
      src: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('src'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.src) return {}
          return { src: attributes.src }
        },
      },
      alt: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('alt'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.alt) return {}
          return { alt: attributes.alt }
        },
      },
      title: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('title'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.title) return {}
          return { title: attributes.title }
        },
      },
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('width'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.width) return {}
          return { width: attributes.width }
        },
      },
      height: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('height'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.height) return {}
          return { height: attributes.height }
        },
      },
      mode: {
        default: 'normal',
        parseHTML: (element: HTMLElement) =>
          element.getAttribute('data-mode') || 'normal',
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.mode) return {}
          return { 'data-mode': attributes.mode }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'img',
        getAttrs: (element: HTMLElement | string) => {
          const domElement = element as HTMLElement
          return {
            src: domElement.getAttribute('src'),
            alt: domElement.getAttribute('alt'),
            title: domElement.getAttribute('title'),
            width: domElement.getAttribute('width'),
            height: domElement.getAttribute('height'),
            mode: domElement.getAttribute('data-mode') || 'normal',
          }
        },
      },
    ]
  },

  // Override renderHTML to ensure all attributes are properly serialized with Tailwind classes
  renderHTML({
    HTMLAttributes,
    node,
  }: {
    HTMLAttributes: Record<string, any>
    node: ProseMirrorNode
  }) {
    const { src, alt, title, width, height, mode } = node.attrs
    const currentMode = mode || 'normal'

    // Base classes for all images
    // origin-center ensures any transforms happen from center
    const baseClasses =
      'block transition-all duration-300 ease-in-out hover:brightness-90 cursor-pointer origin-center'

    // Mode-specific classes
    // Full and wide modes use inline styles for complex calc() values
    const modeClasses = {
      normal: 'w-auto max-w-full mx-auto',
      wide: '', // Wide mode uses inline styles
      full: '', // Full mode uses inline styles
    }

    // Combine classes
    const imageClasses = `${baseClasses} ${modeClasses[currentMode as keyof typeof modeClasses] || modeClasses.normal}`

    // Merge with existing HTMLAttributes classes
    const existingClasses =
      HTMLAttributes.class || HTMLAttributes.className || ''
    const finalClasses = existingClasses
      ? `${existingClasses} ${imageClasses}`.trim()
      : imageClasses

    // For full/wide mode, use calc margins to break out and center
    // The transition-all will smoothly animate width changes
    // To make it expand from center, we ensure margins adjust during transition
    const inlineStyles =
      currentMode === 'full'
        ? {
            width: '100vw',
            maxWidth: '100vw',
            marginLeft: 'calc(50% - 50vw)',
            marginRight: 'calc(50% - 50vw)',
          }
        : currentMode === 'wide'
          ? {
              width: '90vw',
              maxWidth: '90vw',
              marginLeft: 'calc(50% - 45vw)',
              marginRight: 'calc(50% - 45vw)',
            }
          : {}

    return [
      'img',
      {
        ...HTMLAttributes,
        class: finalClasses,
        style: {
          ...(HTMLAttributes.style || {}),
          ...inlineStyles,
        },
        src: src || '',
        ...(alt && { alt }),
        ...(title && { title }),
        ...(width && { width }),
        ...(height && { height }),
        'data-mode': currentMode,
        'data-image-node': 'true', // Specific identifier for image nodes
        id: `tiptap-image-${node.attrs.src?.slice(-8) || Date.now()}`, // Unique ID for querySelector
      },
    ]
  },

  addNodeView() {
    return ({
      node,
      HTMLAttributes,
      getPos,
      editor,
    }: {
      node: ProseMirrorNode
      HTMLAttributes: Record<string, any>
      getPos: () => number | undefined
      editor: any
    }) => {
      // Create a wrapper div to enable centered width transitions
      const wrapper = document.createElement('div')
      wrapper.style.display = 'flex'
      wrapper.style.justifyContent = 'center'
      wrapper.style.transition = 'all 300ms ease-in-out'

      const dom = document.createElement('img')

      // Set all attributes from the node
      const { src, alt, title, width, height, mode } = node.attrs
      const currentMode = mode || 'normal'

      if (src) dom.src = src
      if (alt) dom.alt = alt
      if (title) dom.title = title
      if (width) dom.width = width
      if (height) dom.height = height
      dom.setAttribute('data-mode', currentMode)
      dom.setAttribute('data-image-node', 'true') // Specific identifier for image nodes
      // Add unique ID for reliable querySelector
      const imageId = `tiptap-image-${src?.slice(-8) || Date.now()}`
      dom.id = imageId

      // Base classes for all images
      // origin-center ensures any transforms happen from center
      const baseClasses =
        'block transition-all duration-300 ease-in-out hover:brightness-90 cursor-pointer origin-center'

      // Mode-specific classes
      // Full and wide modes use inline styles for complex calc() values
      const modeClasses = {
        normal: 'w-auto max-w-full mx-auto',
        wide: '', // Wide mode uses inline styles
        full: '', // Full mode uses inline styles
      }

      // Combine classes
      const imageClasses = `${baseClasses} ${modeClasses[currentMode as keyof typeof modeClasses] || modeClasses.normal}`

      // Merge with existing HTMLAttributes classes
      const existingClasses =
        HTMLAttributes.class || HTMLAttributes.className || ''
      const finalClasses = existingClasses
        ? `${existingClasses} ${imageClasses}`.trim()
        : imageClasses

      dom.className = finalClasses

      // For full/wide mode, set width on image, wrapper handles centering
      // This makes width transitions expand from center (both sides)
      if (currentMode === 'full') {
        dom.style.width = '100vw'
        dom.style.maxWidth = '100vw'
        wrapper.style.marginLeft = 'calc(50% - 50vw)'
        wrapper.style.marginRight = 'calc(50% - 50vw)'
      } else if (currentMode === 'wide') {
        dom.style.width = '90vw'
        dom.style.maxWidth = '90vw'
        wrapper.style.marginLeft = 'calc(50% - 45vw)'
        wrapper.style.marginRight = 'calc(50% - 45vw)'
      } else {
        // Normal mode: reset wrapper margins
        wrapper.style.marginLeft = ''
        wrapper.style.marginRight = ''
      }

      // Append image to wrapper
      wrapper.appendChild(dom)

      // Apply other HTML attributes
      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        if (key === 'class' || key === 'className') {
          // Already handled above
          return
        } else if (value !== null && value !== undefined) {
          dom.setAttribute(key, value as string)
        }
      })

      return {
        dom: wrapper, // Return wrapper instead of image
        update: (updatedNode: ProseMirrorNode) => {
          // Only update attributes without replacing the DOM element
          const { src, alt, title, width, height, mode } = updatedNode.attrs
          const updatedMode = mode || 'normal'

          if (src && dom.src !== src) dom.src = src
          if (alt !== undefined) dom.alt = alt || ''
          if (title !== undefined) dom.title = title || ''
          if (width !== undefined) {
            if (width) dom.width = width
            else dom.removeAttribute('width')
          }
          if (height !== undefined) {
            if (height) dom.height = height
            else dom.removeAttribute('height')
          }

          // Update data-mode attribute
          dom.setAttribute('data-mode', updatedMode)
          // Ensure data-image-node is always set
          dom.setAttribute('data-image-node', 'true')
          // Ensure ID is set
          if (!dom.id) {
            const imageId = `tiptap-image-${src?.slice(-8) || Date.now()}`
            dom.id = imageId
          }

          // Update classes based on mode
          const updatedModeClasses =
            modeClasses[updatedMode as keyof typeof modeClasses] ||
            modeClasses.normal
          const updatedImageClasses = `${baseClasses} ${updatedModeClasses}`
          const existingClasses =
            HTMLAttributes.class || HTMLAttributes.className || ''
          const updatedFinalClasses = existingClasses
            ? `${existingClasses} ${updatedImageClasses}`.trim()
            : updatedImageClasses
          dom.className = updatedFinalClasses

          // Update inline styles for full/wide mode
          // Wrapper handles centering, image width transitions from center
          if (updatedMode === 'full') {
            dom.style.width = '100vw'
            dom.style.maxWidth = '100vw'
            wrapper.style.marginLeft = 'calc(50% - 50vw)'
            wrapper.style.marginRight = 'calc(50% - 50vw)'
          } else if (updatedMode === 'wide') {
            dom.style.width = '90vw'
            dom.style.maxWidth = '90vw'
            wrapper.style.marginLeft = 'calc(50% - 45vw)'
            wrapper.style.marginRight = 'calc(50% - 45vw)'
          } else {
            // Reset styles for normal mode
            dom.style.width = ''
            dom.style.maxWidth = ''
            wrapper.style.marginLeft = ''
            wrapper.style.marginRight = ''
          }

          return true
        },
      }
    }
  },
})

export default UpdatedImage
